import { tickToMillisec } from "./tick.js";
/**
 * Class for reading chronological events.
 * Perform lookahead to schedule accurately.
 * https://www.html5rocks.com/ja/tutorials/audio/scheduling/
 */
export class EventScheduler {
    eventSource;
    createLoopEndEvents;
    createStopEvents;
    lookAheadTime = 100;
    // Number of ticks per 1/4 beat
    timebase = 480;
    loop = null;
    _currentTick = 0;
    _scheduledTick = 0;
    _prevTime = undefined;
    _isStopScheduled = false;
    _scheduledSeekTick = null;
    _enqueuedEvents = [];
    constructor(eventSource, createLoopEndEvents, createStopEvents = () => [], tick = 0, lookAheadTime = 100) {
        this.eventSource = eventSource;
        this.createLoopEndEvents = createLoopEndEvents;
        this.createStopEvents = createStopEvents;
        this._currentTick = tick;
        this._scheduledTick = tick;
        this.timebase = this.eventSource.timebase;
        this.lookAheadTime = lookAheadTime;
    }
    get scheduledTick() {
        return this._scheduledTick;
    }
    millisecToTick(ms, bpm) {
        return (((ms / 1000) * bpm) / 60) * this.timebase;
    }
    scheduleSeek(tick) {
        this._scheduledSeekTick = tick;
    }
    scheduleStop() {
        this._isStopScheduled = true;
    }
    // enqueue events to be prepended to the next readNextEvents() result.
    enqueueEvents(events) {
        this._enqueuedEvents = this._enqueuedEvents.concat(events);
    }
    readNextEvents(bpm, timestamp) {
        const withTimestamp = (currentTick) => (e) => {
            const waitTick = e.tick - currentTick;
            const delayedTime = timestamp + Math.max(0, tickToMillisec(waitTick, bpm, this.timebase));
            return { event: e, timestamp: delayedTime };
        };
        const getEventsInRange = (startTick, endTick, currentTick) => this.eventSource
            .getEvents(startTick, endTick)
            .map(withTimestamp(currentTick));
        if (this._prevTime === undefined) {
            this._prevTime = timestamp;
        }
        const delta = timestamp - this._prevTime;
        const deltaTick = Math.max(0, this.millisecToTick(delta, bpm));
        const nowTick = this._currentTick + deltaTick;
        const lookAheadTick = this.millisecToTick(this.lookAheadTime, bpm);
        // Process from the last scheduled point to the lookahead time
        const startTick = this._scheduledTick;
        const endTick = nowTick + lookAheadTick;
        this._prevTime = timestamp;
        if (this._isStopScheduled) {
            this._isStopScheduled = false;
            this._currentTick = nowTick;
            this._scheduledTick = endTick;
            const events = this.createStopEvents().map((e) => withTimestamp(nowTick)({ ...e, tick: endTick }));
            return {
                events,
                shouldStop: true,
            };
        }
        // If a seek has been scheduled, jump to that tick instead of continuing
        // from the current position. If a loop is set, jump to the loop start.
        let jump = null;
        if (this._scheduledSeekTick !== null) {
            jump = {
                from: endTick,
                to: this._scheduledSeekTick,
            };
            this._scheduledSeekTick = null;
        }
        if (this.loop !== null &&
            startTick < this.loop.end &&
            endTick >= this.loop.end) {
            jump = {
                from: this.loop.end,
                to: this.loop.begin,
            };
        }
        const prependedEvents = [];
        if (this._enqueuedEvents.length > 0) {
            prependedEvents.push(...this._enqueuedEvents.map((e) => withTimestamp(nowTick)({ ...e, tick: nowTick })));
            this._enqueuedEvents = [];
        }
        if (jump) {
            const offset = endTick - jump.from;
            const endTick2 = jump.to + offset;
            const currentTick = jump.to - (jump.from - nowTick);
            this._currentTick = currentTick;
            this._scheduledTick = endTick2;
            const events = [
                ...prependedEvents,
                ...getEventsInRange(startTick, jump.from, nowTick),
                ...this.createLoopEndEvents().map((e) => withTimestamp(currentTick)({ ...e, tick: jump.to })),
                ...this.eventSource
                    .getCurrentStateEvents(jump.to)
                    .map((e) => withTimestamp(currentTick)({ ...e, tick: jump.to })),
                ...getEventsInRange(jump.to, endTick2, currentTick),
            ];
            return {
                events,
                shouldStop: false,
            };
        }
        else {
            this._currentTick = nowTick;
            this._scheduledTick = endTick;
            return {
                events: [
                    ...prependedEvents,
                    ...getEventsInRange(startTick, endTick, nowTick),
                ],
                shouldStop: this._scheduledTick >= this.eventSource.endOfSong,
            };
        }
    }
}
//# sourceMappingURL=EventScheduler.js.map